import React, { useState, useEffect } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import {
  ExpansionPanel,
  ExpansionPanelActionEvent,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import { FaFilter } from "react-icons/fa";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import FormTextField from "../../components/formFields/FormTextField";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import IconComponent from "../../components/common/IconComponent";
import { getAllRooms } from "../room/services/room.services";
import {
  resetFilterBookingDetails,
  setFilterBookingDetails,
} from "./bookingSlice";
import { setCurrentPage } from "../pagination/paginationSlice";
import { getAllActiveLeadSource } from "../leadsource/services/leadsource.service";
import { ILeadSource } from "../leadsource/leadsourceModel";

const BookingFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.booking.loading);
  const RoomList = useAppSelector((state) => state.room.RoomList);
  const FilterBookingDetails = useAppSelector(
    (state) => state.booking.FilterBookingDetails,
  );
  const LeadSourceList = useAppSelector(
    (state) => state.leadsource.LeadSourceList,
  );

  useEffect(() => {
    if (!RoomList || RoomList.length === 0) {
      dispatch(getAllRooms());
    }
    if (!LeadSourceList || LeadSourceList.length === 0) {
      dispatch(getAllActiveLeadSource());
    }
  }, []);

  const roomOptions = (RoomList || []).map((room: any) => ({
    label: room.RoomTitle,
    value: room.RoomTitle,
  }));

  const [expand, setExpand] = useState("");
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [FilterBookingDetails]);

  const handleSubmit = (values: any) => {
    const updatedFilter = {
      ...FilterBookingDetails,
      BookingDateFrom: values.BookingDateFrom
        ? moment(values.BookingDateFrom).format("YYYY-MM-DD")
        : null,
      BookingDateTo: values.BookingDateTo
        ? moment(values.BookingDateTo).format("YYYY-MM-DD")
        : null,
      CheckinDateFrom: values.CheckinDateFrom
        ? moment(values.CheckinDateFrom).format("YYYY-MM-DD")
        : null,
      CheckinDateTo: values.CheckinDateTo
        ? moment(values.CheckinDateTo).format("YYYY-MM-DD")
        : null,
      Name: values.Name || "",
      MobileNo: values.MobileNo || "",
      RoomType: values.RoomType || "",
      Source: values.Source || "",
      City: values.City || "",
      ReferalCode: values.ReferalCode || "",
    };
    dispatch(setCurrentPage(0));
    dispatch(setFilterBookingDetails(updatedFilter));
  };

  const handleReset = () => {
    dispatch(setCurrentPage(0));
    dispatch(resetFilterBookingDetails());
  };

  const initialValues = {
    ...FilterBookingDetails,
    BookingDateFrom: FilterBookingDetails?.BookingDateFrom
      ? new Date(FilterBookingDetails.BookingDateFrom)
      : null,
    BookingDateTo: FilterBookingDetails?.BookingDateTo
      ? new Date(FilterBookingDetails.BookingDateTo)
      : null,
    CheckinDateFrom: FilterBookingDetails?.CheckinDateFrom
      ? new Date(FilterBookingDetails.CheckinDateFrom)
      : null,
    CheckinDateTo: FilterBookingDetails?.CheckinDateTo
      ? new Date(FilterBookingDetails.CheckinDateTo)
      : null,
  };

  return (
    <Form
      key={formKey}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement>
          <ExpansionPanel
            className="inquiry-filter-panel"
            style={{ padding: "0" }}
            title={
              <div>
                <div>
                  <IconComponent icon={FaFilter} /> Filter Results
                </div>
              </div>
            }
            expanded={expand === "filter"}
            onAction={(event: ExpansionPanelActionEvent) => {
              if (event?.expanded) {
                setExpand("");
              } else {
                setExpand("filter");
              }
            }}
          >
            {expand === "filter" && (
              <GridLayout
                style={{ margin: "0 50px 20px 20px" }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem>
                  <Field
                    name="BookingDateFrom"
                    label="Booking Date From"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="BookingDateTo"
                    label="Booking Date To"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="CheckinDateFrom"
                    label="Checkin Date From"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="CheckinDateTo"
                    label="Checkin Date To"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Name"
                    label="Customer Name"
                    placeholder="Filter by Name"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="MobileNo"
                    label="Mobile No"
                    placeholder="Filter Mobile No"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="RoomType"
                    label="Room Type"
                    placeholder="Filter Room Type"
                    component={FormSelectionField}
                    options={roomOptions}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Source"
                    label="Source"
                    placeholder="Filter Source"
                    component={FormSelectionField}
                    options={LeadSourceList?.map((leadSource: ILeadSource) => {
                      return {
                        value: leadSource?.Source,
                        label: leadSource?.Source,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="City"
                    label="City"
                    placeholder="Filter City"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={3}
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "32px",
                  }}
                >
                  <ButtonWithLoading
                    label="Search"
                    type="submit"
                    loading={loading}
                    disabled={!formRenderProps.allowSubmit || loading}
                  />
                  <Button
                    type="button"
                    fillMode="outline"
                    themeColor="dark"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            )}
          </ExpansionPanel>
        </FormElement>
      )}
    />
  );
};

export default BookingFilter;
